import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Box, Container } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';

import { getMembers, updateMember } from 'src/api/member';
import Alert from 'src/components/Alert';
import { CONSTANTS, STRINGS } from 'src/commons';
import MemberForm from 'src/components/member/MemberForm';
import { membersReducerAction } from 'src/redux/reducers/members';
import { commonReducerAction } from 'src/redux/reducers/common';
import { loaderReducerAction } from 'src/redux/reducers/loader';

const UpdateMember = () => {
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });
  dayjs.extend(customParseFormat);
  const { startLoading, stopLoading } = loaderReducerAction;
  const dispatch = useDispatch();
  const { user, gymId } = useSelector((state) => state.common);
  const { updateMembers } = membersReducerAction;
  const { logoutUser } = commonReducerAction;
  const { membersList } = useSelector((state) => state.members);
  const { batchList } = useSelector((state) => state.batches);
  const { membershipPlanList } = useSelector((state) => state.membershipPlan);
  const { trainerList } = useSelector((state) => state.trainer);
  const [planName, setPlanName] = useState('');
  const [batchName, setBatchName] = useState('');
  const [trainerName, setTrainerName] = useState('');
  const [dietPlanName, setDietPlanName] = useState('');
  const [number, setNumber] = useState('');
  const [countryCode, setCountryCode] = useState('us');
  const [valid, setValid] = useState(true);
  const { dietPlanList } = useSelector((state) => state.dietPlan);
  
  const HandleChange = (value, code) => {
    setNumber(value);
    setCountryCode(code);
    setValid(validatePhoneNumber(value.slice(code.length)));
  };
  const validatePhoneNumber = (number) => {
    const numberPattern = /^\d{10}/;
    return numberPattern.test(number);
  };

  const showerror = (value) => {
    setValid(validatePhoneNumber(value));
  };

  const [member, setMember] = useState({
    name: '',
    father_name: '',
    email: '',
    address: '',
    phone: '',
    dateOfBirth: '',
    gender: 0,
    weight: '',
    height: '',
    plan: '',
    batch: '',
    trainer: '',
    membership_start_date: '',
    membership_end_date: '',
    payment_reminder_day: '',
    remarks: '',
    profilePic: null,
    _id: ''
  });

  const location = useLocation();
  const memberId = location?.pathname?.split('/')?.pop();

  const getMemberShipPlanId = (name) => {
    const value = membershipPlanList.filter((plan) => plan?.name === name);
    return value[0]._id;
  };
  const getTrainerId = (name) => {
    const value = trainerList.filter((trainer) => trainer?.name === name);
    return value[0]._id;
  };
  const getBatchId = (name) => {
    const value = batchList.filter((batch) => batch?.name === name);
    return value[0]._id;
  };
  const getDietPlanId = (name) => {
    const value = dietPlanList.filter((dietPlan) => dietPlan?.name === name);
    return value[0]._id;
  };

  useEffect(() => {
    if (membersList?.length) {
      const member = membersList.find((member) => member?._id === memberId);
      if (member) {
        setMember(member);
        setBatchName(member?.batch?.name);
        setPlanName(member?.plan?.name);
        setDietPlanName(member?.diet_plan?.name)
        setTrainerName(member?.trainer?.name);
        HandleChange(
          member?.calling_code + member?.phone,
          member?.calling_code
        );
      }
    } else {
      dispatch(startLoading());
      getMembers(user, gymId)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
            dispatch(updateMembers(res?.data?.allMembers));
          }
          dispatch(stopLoading());
        })
        .catch((err) => {
          console.log(err);
          dispatch(stopLoading());
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersList]);

  const navigate = useNavigate();

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };
  return (
    <>
      <Helmet>
        <title>Members | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginTop: '2rem'
        }}
      >
        <Container maxWidth="sm">
          <Formik
            initialValues={{
              memberId: member?.member_id,
              name: member?.full_name,
              fatherName: member.father_name,
              email: member?.email,
              dateOfBirth: dayjs(member?.dob).format('YYYY-MM-DD'),
              address: member.address,
              mobileNumber: member?.phone,
              gender: member?.gender,
              weight: member?.weight,
              height: member?.height,
              membershipPlan: planName,
              dietplan: dietPlanName,
              batch: batchName,
              trainer: trainerName,
              membershipStartDate: dayjs(
                member?.membership_start_date,
                'DD-MM-YYYY'
              ).format('YYYY-MM-DD'),
              membershipEndDate: dayjs(
                member?.membership_end_date,
                'DD-MM-YYYY'
              ).format('YYYY-MM-DD'),
              paymentReminderDay: member.payment_reminder_day,
              remarks: member.remarks,
              profilePic: null
            }}
            enableReinitialize
            validationSchema={CONSTANTS.memberValidationSchema}
            onSubmit={async (values) => {
              const membershipPlan = getMemberShipPlanId(values.membershipPlan);
              const batch = getBatchId(values.batch);
              const trainer = getTrainerId(values.trainer);
              const dietPlan = getDietPlanId(values.dietplan);
              try {
                const formData = new FormData();
                formData.append('full_name', values.name);
                formData.append('father_name', values.fatherName);
                formData.append('email', values.email);
                formData.append('address', values.address);
                // formData.append('phone', values.mobileNumber);
                formData.append('gender', values.gender);
                formData.append('dob', dayjs(values.dateOfBirth));
                formData.append('weight', values.weight);
                formData.append('height', values.height);
                formData.append('plan_id', membershipPlan);
                formData.append('diet_plan_id', dietPlan);
                formData.append('batch_id', batch);
                formData.append('trainer_id', trainer);
                formData.append(
                  'membership_start_date',
                  dayjs(values.membershipStartDate).format('DD-MM-YYYY')
                );
                formData.append(
                  'membership_end_date',
                  dayjs(values.membershipEndDate).format('DD-MM-YYYY')
                );
                formData.append('remarks', values.remarks);
                formData.append('phone', number.slice(countryCode.length));
                formData.append('calling_code', '+' + countryCode);
                if (values.profilePic) {
                  formData.append('profile_pic', values.profilePic);
                }
                dispatch(startLoading());
                const res = await updateMember(user, formData, member._id);
                dispatch(stopLoading());

                const isError = res.status !== 200;
                const message = isError ? res?.message : "Member Updated Successfully"
                setSnackbarState({
                  open: true,
                  message: message,
                  severity: isError ? 'error' : 'success'
                });
                if (!isError) {
                  navigate(-1)
                }
              } catch (err) {
                dispatch(stopLoading());
                setSnackbarState({
                  open: true,
                  message: err.message,
                  severity: 'error'
                });
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              touched,
              values
            }) => (
              // <></>
              <MemberForm
                number={number}
                countryCode={countryCode}
                HandleChange={HandleChange}
                errors={errors}
                handleBlur={handleBlur}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
                touched={touched}
                values={values}
                heading={STRINGS.UPDATE_MEMBER}
                isFromUpdateMember={true}
                showerror={showerror}
              />
            )}
          </Formik>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarState.open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <div>
              <Alert onClose={handleClose} severity={snackbarState.severity}>
                {snackbarState.message}
              </Alert>
            </div>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default UpdateMember;
